import { useCallback, useSyncExternalStore } from "react";

// Editor timeline time per box (box-relative seconds), set by EditorApp while the
// playhead is over the box; null otherwise. Each box follows only its own entry.
const times = new Map();
const subs = new Map();
const notify = (id) => subs.get(id)?.forEach((fn) => fn());

// Bound to the editor session that owns it: a closed session's writes are ignored.
let session = 0;
const owns = (s) => s === undefined || s === session;

export const editorLayerTimes = {
  get: (id) => (times.has(id) ? times.get(id) : null),
  set(id, t, s) {
    if (!owns(s) || times.get(id) === t) return;
    times.set(id, t);
    notify(id);
  },
  clear(id, s) {
    if (owns(s) && times.delete(id)) notify(id);
  },
  reset() {
    const ids = [...times.keys()];
    times.clear();
    ids.forEach(notify);
  },
  // Drops entries of boxes that are gone.
  prune(alive, s) {
    if (!owns(s)) return;
    [...times.keys()].filter((id) => !alive(id)).forEach((id) => this.clear(id));
  },
  startSession() {
    this.reset();
    return ++session;
  },
  endSession(s) {
    if (s !== session) return;
    this.reset();
    session++;
  },
  subscribe(id, fn) {
    let s = subs.get(id);
    if (!s) subs.set(id, (s = new Set()));
    s.add(fn);
    return () => {
      s.delete(fn);
      if (!s.size) subs.delete(id);
    };
  },
};

export function useEditorLayerTime(id) {
  const subscribe = useCallback((fn) => (id ? editorLayerTimes.subscribe(id, fn) : () => {}), [id]);
  return useSyncExternalStore(subscribe, () => (id ? editorLayerTimes.get(id) : null));
}
